import {Inject, Injectable} from '@angular/core';
import { IziviaLoggerService } from './iziviaLogger.service';
import { IziviaMobileTheme } from '../../domain/theme/iziviaMobileTheme';
import { IziviaTheme } from '../../domain/theme/iziviaTheme';
import { THEME } from '../../tokens/theme.token';
import {InAppBrowser, InAppBrowserObject, InAppBrowserOptions} from '@awesome-cordova-plugins/in-app-browser/ngx';
import {TranslateService} from '@ngx-translate/core';
import {IziviaPlatformService} from './iziviaPlatform.service';

@Injectable({
    providedIn: 'root'
})
export class IziviaUriOpenerService {

    constructor(private readonly loggerService: IziviaLoggerService,
        private readonly inAppBrowser: InAppBrowser,
        private readonly translateService: TranslateService,
        private readonly platformService: IziviaPlatformService,
        @Inject(THEME) private readonly theme: IziviaTheme) {
    }

    open(url: string, target: BrowserTarget = BrowserTarget.SYSTEM, options?: UriBrowserOptions, headers?: Record<string, string>): InAppBrowserObject {
        this.loggerService.debug(`IziviaUriOpenerService: open ${url}`);

        if (headers) {
            // usage taken from https://github.com/apache/cordova-plugin-inappbrowser/pull/1024
            const customHttpHeadersStringified = JSON.stringify(headers);

            // base64
            options.headers = btoa(customHttpHeadersStringified).replace('=', '@');
        }

        return this.inAppBrowser.create(url, target, options);
    }

    openInAppPaymentUrl(paymentUrl: string): InAppBrowserObject {
        return this.open(paymentUrl, BrowserTarget.IN_APP, this.getDefaultInAppBrowserOptions());
    }

    getDefaultInAppBrowserOptions(): InAppBrowserOptions {
        const isAndroid = this.platformService.get().is('android');
        const theme = this.theme as IziviaMobileTheme;

        const base: InAppBrowserOptions = {
            hardwareback: 'no',
            transitionstyle: 'coververtical',
            zoom: 'no',
            footer: 'no',
            enableViewportScale: 'yes',
            toolbar: 'yes',
            toolbarposition: 'top',
            toolbartranslucent: 'no',
            hidenavigationbuttons: 'yes',
            hideurlbar: 'yes',
            fullscreen: 'no',
            usewkwebview: 'yes'
        };

        const androidOverrides: Partial<InAppBrowserOptions> = {
            location: 'yes',
            presentationstyle: 'fullscreen',
            toolbarcolor: theme.headerBackgroundColor ?? '#CCCCCC',
            closebuttoncolor: theme.headerTextColor ?? '#FFFFFF',
            closebuttoncaption: this.translateService.instant('common.back'),
            lefttoright: 'yes'
        };

        const iosOverrides: Partial<InAppBrowserOptions> = {
            location: 'no',
            presentationstyle: 'pagesheet',
            toolbarcolor: '#FFFFFF',
            lefttoright: 'no'
        };

        return {
            ...base,
            ...(isAndroid ? androidOverrides : iosOverrides)
        };
    }
}

/**
 * The target in which to load the URL
 */
export enum BrowserTarget {
    /**
     * Opens in the Cordova WebView if the URL is in the white list, otherwise it opens in the InAppBrowser.
     */
    SELF = '_self',

    /**
     * Opens in the InAppBrowser.
     */
    IN_APP = '_blank',

    /**
     * Opens in the system's web browser.
     */
    SYSTEM = '_system'
}

export type UriBrowserOptions = InAppBrowserOptions;
